import React from 'react';
import { PageHero } from '../components/PageHero';
import { Section } from '../components/Section';
import { Card } from '../components/Card';
import { PlaceholderImage } from '../components/PlaceholderImage';
import { IconWrap } from '../components/IconWrap';
import { CiLocationOn } from 'react-icons/ci';
import { partners } from './Home/data/partners';
import useTheme from '../hooks/useTheme';

export const Partners: React.FC = () => {
  const { theme } = useTheme();

  const partnerRoles: Record<string, { bullets: string[]; expectations: string }> = {
    btec: {
      bullets: [
        'Facilitates Korean company connections',
        'Supports market expansion initiatives',
        'Coordinates Busan-based activities',
      ],
      expectations: 'Facilities support / coordination',
    },
    lohmann: {
      bullets: [
        'Serves as bridge between Korea and Egypt',
        'Coordinates missions and introductions',
        'Hosts Cairo office operations',
      ],
      expectations: 'Local bridge / coordination',
    },
    btp: {
      bullets: [
        'Provides facility support in Busan',
        'Hosts meetings and events',
        'Supports infrastructure needs',
      ],
      expectations: 'Facilities support',
    },
  };

  return (
    <>
      <PageHero
        title="Partners"
        subtitle="The founding organizations behind BCBH."
        breadcrumbs={[{ label: 'Home', path: '/' }, { label: 'Partners' }]}
      />

      <Section>
        {partners.map((partner, idx) => (
          <div
            key={partner.id}
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(2, 1fr)',
              gap: '48px',
              marginBottom: idx < partners.length - 1 ? '64px' : '0',
              alignItems: 'center',
            }}
            className="partnerSection"
          >
            <div>
              <h2
                style={{
                  fontSize: '28px',
                  fontWeight: 700,
                  color: theme.text.primary,
                  marginBottom: '12px',
                }}
              >
                {partner.name}
              </h2>
              <div
                style={{
                  fontSize: '16px',
                  fontWeight: 600,
                  color: theme.colors.green.g1,
                  marginBottom: '20px',
                }}
              >
                {partner.role}
              </div>
              <p
                style={{
                  color: theme.text.muted,
                  lineHeight: 1.6,
                  fontSize: '16px',
                  marginBottom: '24px',
                }}
              >
                {partner.description}
              </p>

              <div style={{ marginBottom: '20px' }}>
                <div
                  style={{
                    fontSize: '14px',
                    fontWeight: 600,
                    color: theme.text.primary,
                    marginBottom: '12px',
                  }}
                >
                  Role in BCBH:
                </div>
                <ul
                  style={{
                    listStyle: 'none',
                    padding: 0,
                    margin: 0,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '8px',
                  }}
                >
                  {partnerRoles[partner.id]?.bullets.map((bullet, bulletIdx) => (
                    <li
                      key={bulletIdx}
                      style={{
                        display: 'flex',
                        alignItems: 'flex-start',
                        gap: '8px',
                        fontSize: '14px',
                        color: theme.text.muted,
                      }}
                    >
                      <span style={{ color: theme.colors.green.g1, marginTop: '4px', flexShrink: 0 }}>•</span>
                      {bullet}
                    </li>
                  ))}
                </ul>
              </div>

              <div
                style={{
                  padding: '12px 16px',
                  backgroundColor: theme.background.panel,
                  borderRadius: theme.borderRadius.r1,
                  border: `1px solid ${theme.colors.line}`,
                  fontSize: '13px',
                  color: theme.text.muted,
                }}
              >
                <strong style={{ color: theme.text.primary }}>What to expect:</strong>{' '}
                {partnerRoles[partner.id]?.expectations}
              </div>
            </div>
            <PlaceholderImage label={partner.logoLabel} height={250} />
          </div>
        ))}
      </Section>

      <Section style={{ backgroundColor: theme.background.panel }}>
        <h2
          style={{
            fontSize: '34px',
            fontWeight: 700,
            color: theme.text.primary,
            marginBottom: '32px',
            textAlign: 'center',
          }}
          className="sectionTitle"
        >
          Offices & Presence
        </h2>

        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(2, 1fr)',
            gap: '32px',
          }}
          className="officesGrid"
        >
          <Card hoverable>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                marginBottom: '16px',
              }}
            >
              <IconWrap icon={<CiLocationOn />} size={24} color="primary" />
              <h3
                style={{
                  fontSize: '22px',
                  fontWeight: 600,
                  color: theme.text.primary,
                }}
              >
                Cairo Office
              </h3>
            </div>
            <p
              style={{
                color: theme.text.muted,
                lineHeight: 1.6,
                marginBottom: '16px',
              }}
            >
              BCBH office at LOHMANN ECCE
            </p>
            <PlaceholderImage label="Cairo Office Map" height={200} />
          </Card>

          <Card hoverable>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                marginBottom: '16px',
              }}
            >
              <IconWrap icon={<CiLocationOn />} size={24} color="primary" />
              <h3
                style={{
                  fontSize: '22px',
                  fontWeight: 600,
                  color: theme.text.primary,
                }}
              >
                Busan Office
              </h3>
            </div>
            <p
              style={{
                color: theme.text.muted,
                lineHeight: 1.6,
                marginBottom: '16px',
              }}
            >
              BTEC chairman's company + BTP facility support
            </p>
            <PlaceholderImage label="Busan Office Map" height={200} />
          </Card>
        </div>
      </Section>
    </>
  );
};
